import { api } from "../../redux/apis/api";

/**
 * Shared body for the generated store documents (LOI and Store Agreement).
 *
 * A tab save replaces its storeData section with the save response, which can
 * drop fields, so every value falls back to the /store/getById record.
 */

export const firstFilled = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") ??
  "";

export const calculateAge = (dob) => {
  if (!dob) return "";
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : "";
};

// dd/mm/yyyy, the format dates are printed in.
export const formatDisplayDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const joinParts = (...parts) =>
  parts.map((part) => String(part ?? "").trim()).filter(Boolean).join(", ");

/** Partner rows as /fofo/partnerDetails/getByStoreId returns them; [] on failure. */
export const fetchStorePartners = (storeId) => {
  if (!storeId) return Promise.resolve([]);
  return api
    .get(`/fofo/partnerDetails/getByStoreId?store_id=${storeId}`)
    .then((res) => {
      const list = res.data?.data;
      return Array.isArray(list) ? list : [];
    })
    .catch(() => []);
};

/** Fields both documents carry, read from the store's tabs. */
export const buildDocumentParty = (storeData, record) => {
  const pick = (section, key) =>
    firstFilled(storeData?.[section]?.[key], record?.[key]);

  return {
    firm_type: pick("firm", "firm_type"),
    franchisee_name: pick("personal", "authorised_person_name"),
    // The single title chosen in the application (Mr./Mrs./Ms.), so the
    // document prints that one instead of all three.
    honorific: pick("personal", "honorific"),
    // The authorised person's relation, when the store record carries it.
    relation_type: pick("personal", "relation_type"),
    father_or_spouse: pick("personal", "father_or_spouse"),
    age: calculateAge(pick("personal", "date_of_birth")),
    residential_address: joinParts(
      pick("personal", "residential_address"),
      pick("personal", "residential_city"),
      pick("personal", "residential_state")
    ),
    business_name: pick("firm", "firm_name"),
    pan: pick("firm", "pan_number"),
    phone: pick("personal", "phone_number"),
    email: pick("personal", "email"),
    store_location: joinParts(
      pick("address", "shop_address"),
      pick("address", "shop_city"),
      pick("address", "shop_state")
    ),
    store_code: pick("address", "store_code"),
    date: formatDisplayDate(new Date()),
    franchisor_witness: firstFilled(record?.franchisor_witness),
  };
};

/** The partner fields the LOI prints. */
export const toLoiPartner = (partner) => ({
  partner_name: partner?.partner_name ?? "",
  partner_relation_type: partner?.partner_relation_type ?? "",
  partner_relation_name: partner?.partner_relation_name ?? "",
  age: partner?.age ?? "",
  full_address: partner?.full_address ?? "",
  landmark: partner?.landmark ?? "",
  town_village: partner?.town_village ?? "",
  area: partner?.area ?? "",
  city: partner?.city ?? "",
  state: partner?.state ?? "",
  pincode: partner?.pincode ?? "",
});
